import { useHoldHotkey } from '@/hooks/useHoldHotkey';
import { useCallback, useRef, useState } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useViewerContext } from '@/features/study-detail/Viewer/ViewerContext';
import styles from './DicomButtons.module.css';

const PRESS_DURATION = 250; // ms

interface DicomButtonsProps {
  popupWindow?: Window | null;
}

export default function DicomButtons({ popupWindow }: DicomButtonsProps) {
  const {
    goToNextGroup,
    goToPreviousGroup,
    canGoNext,
    canGoPrevious,
    allPanesPinned,
  } = useViewerContext();

  const [pressed, setPressed] = useState<'prev' | 'next' | null>(null);
  const pressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerPress = (dir: 'prev' | 'next') => {
    setPressed(dir);

    if (pressTimeoutRef.current) {
      clearTimeout(pressTimeoutRef.current);
    }

    pressTimeoutRef.current = setTimeout(() => {
      setPressed(null);
      pressTimeoutRef.current = null;
    }, PRESS_DURATION);
  };

  // With every pane pinned the buttons are disabled, but the attempt is still
  // forwarded: the provider no-ops it and explains why after repeated tries.
  const handlePreviousOnClick = useCallback(() => {
    if (canGoPrevious) triggerPress('prev');
    if (canGoPrevious || allPanesPinned) goToPreviousGroup();
  }, [allPanesPinned, canGoPrevious, goToPreviousGroup]);

  const handleNextOnClick = useCallback(() => {
    if (canGoNext) triggerPress('next');
    if (canGoNext || allPanesPinned) goToNextGroup();
  }, [allPanesPinned, canGoNext, goToNextGroup]);

  useHoldHotkey('ArrowLeft', handlePreviousOnClick, {
    interval: 175,
    targetWindow: popupWindow ?? window,
    // Stop the browser from also scrolling the (just-dragged) thumbnail strip.
    preventDefault: true,
  });

  useHoldHotkey('ArrowRight', handleNextOnClick, {
    interval: 175,
    targetWindow: popupWindow ?? window,
    preventDefault: true,
  });

  return (
    <div className={styles.container}>
      <div
        className={styles.button}
        role="button"
        tabIndex={0}
        aria-disabled={!canGoPrevious}
        data-disabled={!canGoPrevious}
        data-pressed={pressed === 'prev'}
        onClick={handlePreviousOnClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handlePreviousOnClick();
        }}
      >
        <LeftOutlined />
      </div>

      <div
        className={styles.button}
        role="button"
        tabIndex={0}
        aria-disabled={!canGoNext}
        data-disabled={!canGoNext}
        data-pressed={pressed === 'next'}
        onClick={handleNextOnClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleNextOnClick();
        }}
      >
        <RightOutlined />
      </div>
    </div>
  );
}
